import { useState, useEffect, useRef } from 'react'
import { Check, ChevronsUpDown, User, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface DonorResult {
  id: number
  type: string
  name: string
  email: string | null
}

interface DonorComboboxProps {
  value?: { id: number; type: string; name: string } | null
  onChange: (donor: { id: number; type: string; name: string } | null) => void
  error?: string
  /**
   * When true, the combobox renders the selected donor as a read-only chip
   * without the search popover or clear button. Intended for pre-filled
   * flows (e.g. the "Nouveau don" CTA from DonorDetailSheet) where the
   * donor is fixed by the caller.
   *
   * Uses aria-readonly rather than `disabled` so screen readers announce
   * the selection as pre-filled, not unavailable.
   */
  locked?: boolean
}

const DEBOUNCE_MS = 300
const MIN_CHARS = 2

function donorTypeLabel(type: string): string {
  if (type.includes('IndividualDonor')) return 'Individuel'
  if (type.includes('OrganizationDonor')) return 'Organisation'
  return type
}

function DonorIcon({ type }: { type: string }) {
  if (type.includes('OrganizationDonor')) {
    return <Building2 className="h-4 w-4 text-muted-foreground" />
  }
  return <User className="h-4 w-4 text-muted-foreground" />
}

export function DonorCombobox({ value, onChange, error, locked = false }: DonorComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DonorResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  if (locked && value) {
    return (
      <div className="space-y-1">
        <div
          role="combobox"
          aria-readonly="true"
          aria-label="Donateur pre-selectionne"
          className={cn(
            'flex w-full items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm'
          )}
        >
          <DonorIcon type={value.type} />
          <span className="truncate">{value.name}</span>
          <Badge variant="outline" className="ml-auto text-xs">
            {donorTypeLabel(value.type)}
          </Badge>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    )
  }

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (query.length < MIN_CHARS) {
      setResults([])
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/fundraising/donors/search?q=${encodeURIComponent(query)}`
        )
        if (res.ok) {
          const data: DonorResult[] = await res.json()
          setResults(data)
        }
      } catch {
        // Silently handle network errors
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query])

  function handleSelect(donor: DonorResult) {
    onChange({ id: donor.id, type: donor.type, name: donor.name })
    setOpen(false)
    setQuery('')
  }

  function handleClear() {
    onChange(null)
    setQuery('')
  }

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? (
              <span className="flex items-center gap-2 truncate">
                <DonorIcon type={value.type} />
                {value.name}
              </span>
            ) : (
              'Rechercher un donateur...'
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Nom ou email du donateur..."
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading && (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  Recherche...
                </div>
              )}
              {!loading && query.length >= MIN_CHARS && results.length === 0 && (
                <CommandEmpty>Aucun donateur trouve.</CommandEmpty>
              )}
              {!loading && query.length < MIN_CHARS && (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  Saisissez au moins {MIN_CHARS} caracteres.
                </div>
              )}
              {results.length > 0 && (
                <CommandGroup>
                  {results.map((donor) => (
                    <CommandItem
                      key={`${donor.type}-${donor.id}`}
                      value={`${donor.type}-${donor.id}`}
                      onSelect={() => handleSelect(donor)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value?.id === donor.id && value?.type === donor.type
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <DonorIcon type={donor.type} />
                      <div className="ml-2 flex flex-col">
                        <span className="text-sm font-medium">{donor.name}</span>
                        {donor.email && (
                          <span className="text-xs text-muted-foreground">{donor.email}</span>
                        )}
                      </div>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {donorTypeLabel(donor.type)}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline"
          onClick={handleClear}
        >
          Effacer la selection
        </button>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
